/**
 * File: apps/qbench-cp/reactapp/src/shared/layout/PortalTopNav/Logo/Logo.jsx
 *
 * Purpose: Renders the customer's uploaded logo as a link back to the portal home page.
 *
 * Scope: Portal top navigation branding for the customer portal.
 *
 * Tags: customer-portal
 *
 * Overview: Memoized component that renders nothing when no customPortalLogo blob is supplied,
 *     otherwise creates an object URL for the logo blob and displays it (using a static mobile
 *     logo asset as an additional fallback in the mobile branch). Clicking the logo while inside
 *     the create-order flow dispatches a RESET_CREATE_ORDER action instead of navigating
 *     directly, so in-progress order state is handled consistently with other nav links.
 *
 * Exports: Logo component (default export, React.memo-wrapped).
 */

import { useAppStore } from 'App/store';
import logoMobile from 'assets/images/png/logoMobile.png';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CREATE_ORDER_PATH } from 'shared/layout/MainMenu';
import styled from 'styled-components';

const StyledLogo = styled(Link) `
	display: flex;
	justify-content: center;
	align-items: center;
    margin-right: 20px;
	& > img {
		max-height: 44px;
		@media ${({theme}) => theme.device.mobile} {
			max-height: 36px;
		}
	}

	@media ${({theme}) => theme.device.mobile} {
		margin-right: 10px;
	}
`;

const Logo = React.memo(({isMobile, customPortalLogo}) => {
	const { pathname } = useLocation();
	const { appStateDispatch } = useAppStore();

  function handlePreClick(targetPathname) {
    return (e) => {
      const isExitingCreateOrder =
        pathname.includes(CREATE_ORDER_PATH) && !targetPathname.includes(CREATE_ORDER_PATH);
      if (isExitingCreateOrder) {
        e.preventDefault();
        appStateDispatch({ type: 'RESET_CREATE_ORDER', payload: targetPathname });
      } else {
        appStateDispatch({ type: 'RESET_CREATE_ORDER', payload: false });
      }
    };
  }

	if(!customPortalLogo) return null;

	return (
		<StyledLogo to="/portal" onClick={handlePreClick('/portal')}>
			{isMobile
            	? <img src={customPortalLogo ? URL.createObjectURL(customPortalLogo) : logoMobile} alt='logo' />
				: <img src={URL.createObjectURL(customPortalLogo)} alt='logo' />
			}
		</StyledLogo>
	);
});

export default Logo;
